import React, { useState, useEffect } from 'react';
import { X, Plus, Search, Camera } from 'lucide-react';
import { Photo } from '../types';
import { getPhotosByProject } from '../services/photoService';

interface PhotoSelectorProps {
  projectId: string;
  excludePhotoIds?: string[];
  onPhotoSelected: (photo: Photo) => void;
  onClose: () => void;
}

const PhotoSelector: React.FC<PhotoSelectorProps> = ({
  projectId,
  excludePhotoIds = [],
  onPhotoSelected,
  onClose
}) => {
  const [availablePhotos, setAvailablePhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        const allPhotos = await getPhotosByProject(projectId);
        // Filter out photos that are already selected/excluded
        const filteredPhotos = allPhotos.filter(photo => !excludePhotoIds.includes(photo.id));
        setAvailablePhotos(filteredPhotos);
        setError(null);
      } catch (err) {
        console.error('Error fetching project photos:', err);
        setError('Kunde inte hämta projektfoton');
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, [projectId, excludePhotoIds]);

  const filteredPhotos = availablePhotos.filter(photo => {
    if (!searchTerm) return true;
    return (
      photo.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      photo.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const handlePhotoSelect = (photo: Photo) => {
    onPhotoSelected(photo);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-surface)] rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-default)]">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Välj foto från projektbibliotek</h2>
          <button
            onClick={onClose}
            aria-label="Stäng"
            className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-[var(--border-default)]">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-[var(--text-tertiary)]" />
            <input
              type="text"
              placeholder="Sök i anteckningar eller foto-ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-[var(--bg-surface-alt)] border border-[var(--border-default)] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] rounded-lg focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-500"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <Camera size={48} className="mx-auto text-[var(--text-tertiary)] opacity-40 mb-4" />
              <p className="text-[var(--text-secondary)] text-base">{error}</p>
            </div>
          ) : filteredPhotos.length === 0 ? (
            <div className="text-center py-8">
              <Camera size={48} className="mx-auto text-[var(--text-tertiary)] opacity-40 mb-4" />
              <p className="text-[var(--text-secondary)] text-base">
                {searchTerm ? 'Inga foton hittades' : 'Inga tillgängliga foton i projektbiblioteket'}
              </p>
              <p className="text-[var(--text-tertiary)] text-sm mt-2">
                {!searchTerm && 'Ta foton först eller alla foton är redan kopplade till detta objekt'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filteredPhotos.map((photo) => (
                <div
                  key={photo.id}
                  className="relative aspect-square border border-[var(--border-default)] rounded-lg overflow-hidden cursor-pointer hover:border-accent-500 hover:shadow-md transition-all group"
                  onClick={() => handlePhotoSelect(photo)}
                >
                  <img
                    src={photo.thumbnail || photo.url}
                    alt="Projektfoto"
                    className="w-full h-full object-cover"
                  />

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                    <div className="bg-accent-500 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-200">
                      <Plus size={20} />
                    </div>
                  </div>

                  {/* Photo info */}
                  {photo.notes && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-white text-xs truncate">{photo.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[var(--border-default)] bg-[var(--bg-surface-alt)]">
          <div className="flex justify-between items-center text-sm text-[var(--text-secondary)]">
            <span>
              {filteredPhotos.length} av {availablePhotos.length} foton
              {searchTerm && ` (filtrerat)`}
            </span>
            <button
              onClick={onClose}
              className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
            >
              Avbryt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoSelector;
